import React from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Avatar,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import MuiTooltip from "@material-ui/core/Tooltip";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { useHistory } from "react-router-dom";
import defaultAvatars from "../../../../../assets/defaultAvatars.svg";
import {
  ActiveSessionRow,
  UserIpRow,
  UsersLastIpRow,
} from "./interfaceDashboard";
import {
  VisitorApiData,
  flagEmojiFromIso3166Alpha2,
} from "../../../../../utils/visitorApi";

function AdminVisitorDetailPanel({
  visitor,
  fallbackIp,
  fallbackUa,
}: {
  visitor?: VisitorApiData;
  fallbackIp?: string;
  fallbackUa?: string;
}) {
  const fmtArr = (a?: string[]) =>
    a?.filter(Boolean).length ? a!.filter(Boolean).join(", ") : "—";
  const row = (label: string, value: React.ReactNode) => (
    <Box
      key={label}
      display="flex"
      flexWrap="wrap"
      alignItems="baseline"
      mb={0.5}
      style={{ gap: 8 }}
    >
      <Typography
        component="span"
        style={{ opacity: 0.7, minWidth: 148, fontSize: 11 }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        style={{ fontSize: 11, flex: 1, wordBreak: "break-word" }}
      >
        {value ?? "—"}
      </Typography>
    </Box>
  );

  const flag = flagEmojiFromIso3166Alpha2(visitor?.countryCode);
  const hasVisitor =
    !!visitor && Object.keys(visitor).some(k => (visitor as any)[k]);
  const snapshotIp = visitor?.ipAddress || "";
  const serverIp = fallbackIp || "";
  const shouldShowServerIp = !!serverIp && (!snapshotIp || snapshotIp !== serverIp);

  return (
    <Box style={{ marginBottom: 12 }}>
      <Typography
        style={{
          fontSize: 12,
          fontWeight: 600,
          marginBottom: 6,
          color: "#a78bfa",
        }}
      >
        Location, language and currency (Visitor API snapshot)
      </Typography>
      {!hasVisitor ? (
        <Typography style={{ fontSize: 11, opacity: 0.75 }}>
          No Visitor API snapshot on this row yet. When the backend stores
          visitorapi.com fields on the session, they will appear here.
        </Typography>
      ) : (
        <>
          {row(
            "Country",
            <>
              {flag ? `${flag} ` : ""}
              {visitor!.countryName || "—"}
              {visitor!.countryCode ? ` (${visitor!.countryCode})` : ""}
            </>
          )}
          {row("Region", visitor!.region)}
          {row("City", visitor!.city)}
          {row("Latitude / longitude", visitor!.cityLatLong)}
          {row("Languages (ISO 639-3)", fmtArr(visitor!.languages))}
          {row("Currencies", fmtArr(visitor!.currencies))}
          {row("IP", visitor!.ipAddress)}
        </>
      )}
      <Typography
        style={{
          fontSize: 12,
          fontWeight: 600,
          marginTop: 10,
          marginBottom: 6,
          color: "#a78bfa",
        }}
      >
        Browser, device and OS
      </Typography>
      {row(
        "Browser",
        visitor?.browser
          ? `${visitor.browser}${
              visitor.browserVersion ? ` ${visitor.browserVersion}` : ""
            }`
          : "—"
      )}
      {row(
        "Operating system",
        visitor?.os
          ? `${visitor.os}${
              visitor.osVersion ? ` ${visitor.osVersion}` : ""
            }`
          : "—"
      )}
      {row(
        "Device",
        [visitor?.deviceBrand, visitor?.deviceModel, visitor?.deviceFamily]
          .filter(Boolean)
          .join(" · ") || "—"
      )}
      <Typography
        style={{
          fontSize: 12,
          fontWeight: 600,
          marginTop: 10,
          marginBottom: 6,
          color: "#94a3b8",
        }}
      >
        Session record (server)
      </Typography>
      {shouldShowServerIp ? row("Server IP", serverIp) : null}
      {row("User-Agent", fallbackUa || "—")}
    </Box>
  );
}

export type AdminActiveSessionsClasses = {
  subscribersBox: string;
  sectionTitle: string;
  subscribersBoxx: string;
  subscriberItem: string;
  subscriberItemm: string;
  avatar: string;
  name: string;
  username: string;
};

export type AdminActiveSessionsPanelProps = {
  classes: AdminActiveSessionsClasses;
  securityLoading: boolean;
  allHistoryLoading: boolean;
  showAllHistory: boolean;
  toggleShowAllHistory: () => void;
  sessionsNow: ActiveSessionRow[];
  allHistoryData: ActiveSessionRow[];
  expandedUsers: Set<string>;
  toggleUserExpansion: (userId: string) => void | Promise<void>;
  userIpHistories: Record<string, UserIpRow[]>;
  loadingIpHistories: Set<string>;
  expandedLastIpUsers: Set<string>;
  toggleLastIpUserExpansion: (userId: string) => void;
  usersLastIPs: UsersLastIpRow[];
  mediaUrls: Record<string, string>;
  loadingMedia: Record<string, boolean>;
  fmtDateTime: (iso?: string) => string;
  openUserIpHistory: (userId: string, username?: string) => void;
};

const AdminActiveSessionsPanel: React.FC<AdminActiveSessionsPanelProps> = ({
  classes,
  securityLoading,
  allHistoryLoading,
  showAllHistory,
  toggleShowAllHistory,
  sessionsNow,
  allHistoryData,
  expandedUsers,
  toggleUserExpansion,
  userIpHistories,
  loadingIpHistories,
  expandedLastIpUsers,
  toggleLastIpUserExpansion,
  usersLastIPs,
  mediaUrls,
  loadingMedia,
  fmtDateTime,
  openUserIpHistory,
}) => {
  const history = useHistory();

  return (
    <Grid container spacing={2} style={{ marginTop: 16 }}>
      <Grid item xs={12} md={6}>
        <Box className={classes.subscribersBox}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <Typography className={classes.sectionTitle}>
              Active Creators (last 30m)
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={toggleShowAllHistory}
              disabled={allHistoryLoading}
              style={{
                color: "#FE5A97",
                borderColor: "#FE5A97",
                textTransform: "none",
                fontSize: "12px",
                padding: "4px 8px",
                minWidth: "auto",
              }}
            >
              {allHistoryLoading
                ? "Loading..."
                : showAllHistory
                ? "Show Recent"
                : "Show All History"}
            </Button>
          </div>
          <Box
            style={{ maxHeight: 420, overflowY: "auto" }}
            className={classes.subscribersBoxx}
          >
            {securityLoading || allHistoryLoading ? (
              <CircularProgress />
            ) : (showAllHistory ? allHistoryData : sessionsNow)?.length ===
              0 ? (
              <Box p={2} style={{ opacity: 0.7 }}>
                {showAllHistory
                  ? "No active creators found in the last 24 hours."
                  : "No active creators in the last 30 minutes."}
              </Box>
            ) : (
              (showAllHistory ? allHistoryData : sessionsNow).map(s => {
                const userId = String(s.userId);
                const isExpanded = expandedUsers.has(userId);
                const ipHistory = userIpHistories[userId] || [];
                const isLoadingIpHistory = loadingIpHistories.has(userId);
                const uname = (s.username || "").trim();
                const profilePath = uname
                  ? `/${encodeURIComponent(uname)}`
                  : "";
                const vf = flagEmojiFromIso3166Alpha2(s.visitor?.countryCode);

                return (
                  <Box key={userId}>
                    <Box
                      className={classes.subscriberItem}
                      role="button"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleUserExpansion(userId)}
                      onKeyPress={e => {
                        if (e.key === "Enter") toggleUserExpansion(userId);
                      }}
                    >
                      <div className={classes.subscriberItemm}>
                        <Avatar
                          src={
                            mediaUrls[s.profileImg || ""] || defaultAvatars
                          }
                          className={classes.avatar}
                        >
                          {loadingMedia[s.profileImg || ""] && (
                            <CircularProgress size={24} />
                          )}
                        </Avatar>

                        <Box style={{ minWidth: 0 }}>
                          <Typography className={classes.name}>
                            {s.username || s.email || "User"}
                          </Typography>

                          <Typography className={classes.username}>
                            {fmtDateTime(s.lastSeenAt)}
                          </Typography>

                          {(s.visitor?.countryCode || s.visitor?.city) && (
                            <Typography className={classes.username}>
                              {vf ? `${vf} ` : ""}
                              {s.visitor?.countryName ||
                                s.visitor?.countryCode ||
                                ""}
                              {s.visitor?.city ? ` · ${s.visitor.city}` : ""}
                            </Typography>
                          )}

                          <Typography
                            className={classes.username}
                            title={s.ua || ""}
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: 360,
                            }}
                          >
                            {s.ua || "—"}
                          </Typography>
                        </Box>
                      </div>

                      <Box
                        display="flex"
                        alignItems="center"
                        onClick={e => e.stopPropagation()}
                      >
                        {profilePath ? (
                          <MuiTooltip title="Open profile">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={e => {
                                e.stopPropagation();
                                history.push(profilePath);
                              }}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </MuiTooltip>
                        ) : null}
                        <MuiTooltip
                          title={
                            isExpanded
                              ? "Hide details"
                              : "Show IP history and visitor details"
                          }
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => toggleUserExpansion(userId)}
                          >
                            {isExpanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </MuiTooltip>
                      </Box>
                    </Box>

                    {isExpanded && (
                      <Box
                        style={{
                          marginLeft: 16,
                          marginTop: 8,
                          padding: 12,
                          backgroundColor: "#1a1a1a",
                          borderRadius: 8,
                          border: "1px solid #333",
                        }}
                      >
                        <AdminVisitorDetailPanel
                          visitor={s.visitor}
                          fallbackIp={s.ip}
                          fallbackUa={s.ua}
                        />
                        <Typography
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            marginBottom: 8,
                            color: "#FE5A97",
                          }}
                        >
                          IP History ({ipHistory.length} entries)
                        </Typography>

                        {isLoadingIpHistory ? (
                          <Box
                            display="flex"
                            justifyContent="center"
                            py={2}
                          >
                            <CircularProgress size={20} />
                          </Box>
                        ) : ipHistory.length === 0 ? (
                          <Typography style={{ opacity: 0.7, fontSize: 12 }}>
                            No IP history found for this user.
                          </Typography>
                        ) : (
                          <Box
                            style={{
                              maxHeight: 200,
                              overflowY: "auto",
                            }}
                          >
                            {ipHistory.map((ip, idx) => (
                              <Box
                                key={`${ip.IP}-${idx}`}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "4px 0",
                                  borderBottom: "1px solid #333",
                                  fontSize: 12,
                                }}
                              >
                                <Box>
                                  <Typography
                                    style={{
                                      fontFamily: "monospace",
                                      color: "#fff",
                                      fontSize: 11,
                                    }}
                                  >
                                    {ip.IP}
                                  </Typography>
                                  <Typography
                                    style={{
                                      opacity: 0.7,
                                      fontSize: 10,
                                    }}
                                  >
                                    {fmtDateTime(ip.Time)} • {ip.Hits} hits
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box className={classes.subscribersBox}>
          <Typography className={classes.sectionTitle}>
            Active Users (last activity, 30d)
          </Typography>
          <Box
            style={{ maxHeight: 420, overflowY: "auto" }}
            className={classes.subscribersBoxx}
          >
            {securityLoading ? (
              <CircularProgress />
            ) : (usersLastIPs?.length ?? 0) === 0 ? (
              <Box p={2} style={{ opacity: 0.7 }}>
                No users with sessions in the last 30 days.
              </Box>
            ) : (
              usersLastIPs.map(u => {
                const uid = String(u.userId);
                const lastIpExpanded = expandedLastIpUsers.has(uid);
                const uname = (u.username || "").trim();
                const profilePath = uname
                  ? `/${encodeURIComponent(uname)}`
                  : "";
                const vf = flagEmojiFromIso3166Alpha2(u.visitor?.countryCode);
                return (
                  <Box key={uid}>
                    <Box
                      className={classes.subscriberItem}
                      role="button"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        openUserIpHistory(uid, u.username || u.email || "User")
                      }
                      onKeyPress={e => {
                        if (e.key === "Enter")
                          openUserIpHistory(
                            uid,
                            u.username || u.email || "User"
                          );
                      }}
                    >
                      <div className={classes.subscriberItemm}>
                        <Avatar
                          src={
                            mediaUrls[u.profileImg || ""] || defaultAvatars
                          }
                          className={classes.avatar}
                        >
                          {loadingMedia[u.profileImg || ""] && (
                            <CircularProgress size={24} />
                          )}
                        </Avatar>

                        <Box style={{ minWidth: 0 }}>
                          <Typography className={classes.name}>
                            {u.username || u.email || "User"}
                          </Typography>

                          <Typography className={classes.username}>
                            {fmtDateTime(u.lastAt)}
                          </Typography>

                          {(u.visitor?.countryCode || u.visitor?.city) && (
                            <Typography className={classes.username}>
                              {vf ? `${vf} ` : ""}
                              {u.visitor?.countryName ||
                                u.visitor?.countryCode ||
                                ""}
                              {u.visitor?.city ? ` · ${u.visitor.city}` : ""}
                            </Typography>
                          )}

                          <Typography
                            className={classes.username}
                            title={u.ua || ""}
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: 360,
                            }}
                          >
                            {u.ua || "—"}
                          </Typography>
                        </Box>
                      </div>
                      <Box
                        display="flex"
                        alignItems="center"
                        onClick={e => e.stopPropagation()}
                      >
                        {profilePath ? (
                          <MuiTooltip title="Open profile">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={e => {
                                e.stopPropagation();
                                history.push(profilePath);
                              }}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </MuiTooltip>
                        ) : null}
                        <MuiTooltip
                          title={
                            lastIpExpanded
                              ? "Hide visitor details"
                              : "Show visitor details"
                          }
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => toggleLastIpUserExpansion(uid)}
                          >
                            {lastIpExpanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </MuiTooltip>
                      </Box>
                    </Box>
                    {lastIpExpanded && (
                      <Box
                        style={{
                          marginLeft: 16,
                          marginTop: 8,
                          padding: 12,
                          backgroundColor: "#1a1a1a",
                          borderRadius: 8,
                          border: "1px solid #333",
                        }}
                      >
                        <AdminVisitorDetailPanel
                          visitor={u.visitor}
                          fallbackIp={u.ip}
                          fallbackUa={u.ua}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default AdminActiveSessionsPanel;
